/**
 * EvidencePreservation — quiet editorial-transparency section on dossiers.
 *
 * Shown only when a dossier cites recovered (archived) evidence. Explains
 * what changed, why, and how the Archive handled it — provenance, not
 * promotion. All values come from the deterministic preservation endpoint.
 */
import React, { useState } from "react";
import axios from "axios";
import { DossierLabel } from "../dossier/DossierLabel";

const API = process.env.REACT_APP_BACKEND_URL;

const CONF_LABEL = {
  very_high: "Very High", high: "High", moderate: "Moderate", low: "Low",
};

const Field = ({ label, children }) => (
  <div className="flex flex-col sm:flex-row sm:gap-2">
    <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#757065] sm:w-44 shrink-0">
      {label}
    </span>
    <span className="font-mono text-[10px] text-[#A39E93] break-all">{children}</span>
  </div>
);

export const EvidencePreservation = ({ sources = [], topicSlug }) => {
  const recovered = sources.filter((s) => s.recovered);
  const [open, setOpen] = useState(false);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  if (!recovered.length || !topicSlug) return null;

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !details && !loading) {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/api/evidence-preservation/${topicSlug}`);
        setDetails(r.data.items || []);
      } catch { setDetails([]); }
      finally { setLoading(false); }
    }
  };

  const n = recovered.length;
  return (
    <div className="mt-10 border-t border-[#2D2A26] pt-6" data-testid="evidence-preservation">
      <DossierLabel as="h3" className="mt-0">Evidence Preservation</DossierLabel>
      <p className="mt-3 text-[13px] leading-relaxed text-[#A39E93] max-w-2xl">
        This investigation cites {n} archived source{n === 1 ? "" : "s"} preserved
        after the original publication{n === 1 ? "" : "s"} became unavailable.
      </p>
      <p className="mt-2 font-mono text-[10px] leading-relaxed text-[#757065] max-w-2xl">
        The Archive preserves verified archived copies to maintain the historical
        evidence trail while recording that the original source is no longer available.
      </p>
      <button type="button" onClick={toggle} data-testid="preservation-details-toggle"
              className="mt-3 inline-flex items-center border border-[#454038] px-3 py-2 font-mono text-[9px] uppercase tracking-[0.25em] text-[#A39E93] hover:text-[#E4DFD3] hover:border-[#757065] transition-colors">
        {open ? "Hide Preservation Details" : "View Preservation Details"}
      </button>

      {open && (
        <div className="mt-4 space-y-4" data-testid="preservation-details">
          {loading && (
            <p className="font-mono text-[10px] text-[#757065]">Retrieving preservation record…</p>
          )}
          {(details || []).map((d, i) => (
            <div key={i} className="border border-[#2D2A26] bg-[#111110] p-4 space-y-1.5"
                 data-testid={`preservation-item-${i}`}>
              <div className="text-[13px] text-[#D5CFC2]">{d.title}</div>
              <Field label="Original source">{d.original_url}</Field>
              <Field label="Current status">{d.current_status}</Field>
              {d.archived_snapshot_date && (
                <Field label="Archived snapshot date">{d.archived_snapshot_date}</Field>
              )}
              {d.recovery_confidence && (
                <Field label="Recovery confidence">
                  {CONF_LABEL[d.recovery_confidence] || d.recovery_confidence}
                </Field>
              )}
              <Field label="Selection reason">{d.selection_reason}</Field>
              {d.recovery_date && <Field label="Recovery date">{d.recovery_date}</Field>}
              <Field label="Editorial review">{d.editorial_review}</Field>
            </div>
          ))}
          {details && details.length === 0 && !loading && (
            <p className="font-mono text-[10px] text-[#757065]">
              Preservation record unavailable.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default EvidencePreservation;
